import { useEffect, useLayoutEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/*
 * Site-wide motion controller (styles in src/styles/motion.css).
 *
 * - Sections fade/rise in as they enter the viewport; cards inside grids and lists rise in a
 *   short stagger; large standalone images get a soft clip reveal.
 * - Content that arrives later from the CMS is picked up by a MutationObserver.
 * - Anything already scrolled past (anchor jumps, End key, restored scroll) is revealed at once,
 *   so a section can never stay hidden.
 * - Hero visuals marked with data-parallax drift slightly on scroll (desktop only).
 * Nothing is hidden or moved when the visitor prefers reduced motion or IO is unavailable.
 */

const HERO = ".hero, .ab-hero, .jj-hero, .gv-hero, [data-no-reveal]";
const SECTIONS = "main section";
// card collections: grids and lists of cards across all pages
const GROUPS = 'main ul[class*="grid"], main ol[class*="grid"], main div[class*="-grid"], main ul[class*="-list"], main ol[class*="-list"], main div[class*="-list"]';
const STANDALONE_IMG = "main section figure img, main section .ab-photo img";
const PARALLAX = ".ih-visual, .jj-hero-plate, .gv-hero-plate";
const MAX_STAGGER = 8;

function prefersReduced() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function insideExcluded(el: Element) {
  return !!el.closest(`${HERO}, dialog, [role="dialog"], form, nav, .dropdown, [role="tablist"], [data-no-reveal]`);
}

export function ScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!("IntersectionObserver" in window) || prefersReduced()) {return;}
    const root = document.documentElement;
    root.classList.add("motion-ok");

    const settle = (el: HTMLElement) => {
      const done = () => el.classList.add("is-settled");
      el.addEventListener("transitionend", done, { once: true });
      window.setTimeout(done, 1600);
    };

    const io = new IntersectionObserver(
      (entries) => {
        let n = 0;
        for (const e of entries) {
          const el = e.target as HTMLElement;
          const passed = e.boundingClientRect.bottom < 0;
          if (!e.isIntersecting && !passed) {continue;}
          io.unobserve(el);
          const delay = el.dataset.reveal === "item" && !passed ? Math.min(n++, MAX_STAGGER) * 70 : 0;
          el.style.setProperty("--reveal-delay", `${delay}ms`);
          el.classList.add("is-revealed");
          settle(el);
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.04 },
    );

    const tag = (el: Element, kind: "section" | "item" | "image") => {
      if (el.hasAttribute("data-reveal")) {return;}
      // already scrolled past or on screen at load: no hidden flash for above-the-fold content
      const r = el.getBoundingClientRect();
      if (r.bottom < 0) {return;}
      el.setAttribute("data-reveal", kind);
      io.observe(el);
    };

    const scan = () => {
      document.querySelectorAll(SECTIONS).forEach((s) => {
        if (s.matches(HERO) || s.closest(HERO) || s.closest("[data-reveal]")) {return;}
        tag(s, "section");
      });
      document.querySelectorAll(GROUPS).forEach((g) => {
        if (insideExcluded(g) || g.children.length < 2) {return;}
        Array.from(g.children).forEach((c) => tag(c, "item"));
      });
      document.querySelectorAll(STANDALONE_IMG).forEach((img) => {
        if (insideExcluded(img) || img.closest("[data-reveal='item']")) {return;}
        if ((img as HTMLImageElement).getBoundingClientRect().width < 220) {return;}
        tag(img, "image");
      });
    };

    let raf = requestAnimationFrame(scan);
    const main = document.getElementById("main");
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(scan);
    });
    if (main) {mo.observe(main, { childList: true, subtree: true });}

    // self-heal: when scrolling settles, nothing above the bottom of the viewport stays hidden
    let idle = 0;
    const heal = () => {
      const vh = window.innerHeight;
      document.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-revealed)").forEach((el) => {
        if (el.getBoundingClientRect().top < vh) {
          io.unobserve(el);
          el.classList.add("is-revealed");
          settle(el);
        }
      });
    };
    const onScroll = () => {
      window.clearTimeout(idle);
      idle = window.setTimeout(heal, 140);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const revealAll = () => document.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("is-revealed", "is-settled"));
    window.addEventListener("beforeprint", revealAll);

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      io.disconnect();
      window.removeEventListener("beforeprint", revealAll);
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(idle);
      // anything left un-revealed on the outgoing route must not stay hidden if reused
      revealAll();
    };
  }, [pathname]);

  // gentle parallax on inner-page hero visuals
  useEffect(() => {
    if (prefersReduced() || !window.matchMedia("(min-width: 900px)").matches) {return;}
    let els: HTMLElement[] = [];
    let raf = 0;
    const collect = () => {
      els = Array.from(document.querySelectorAll<HTMLElement>(PARALLAX));
      els.forEach((el) => el.setAttribute("data-parallax", ""));
    };
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) {continue;}
        const speed = Number(el.dataset.parallaxSpeed ?? 0.08);
        const y = Math.max(-36, Math.min(36, -window.scrollY * speed));
        el.style.setProperty("--parallax-y", `${y.toFixed(1)}px`);
      }
    };
    const onScroll = () => {
      if (!raf) {raf = requestAnimationFrame(update);}
    };
    const t = window.setTimeout(() => {
      collect();
      update();
    }, 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(t);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  return null;
}

/** Scroll to the top (or to #hash) whenever the route changes. */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

/** Soft fade/rise between routes. Keyed by path so each page plays the entrance once. */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="page-transition">
      {children}
    </div>
  );
}
